import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from './Toast';
import { formatDate } from '../format';
import { addGroupMember, listGroups, removeGroupMember, updateGroupMemberRole } from '../api/groups';
import type { CurrentUser, Group, GroupRole, ManagedUser, ManagedUserGroup } from '../api/types';
import { grantBulkEditor, listUsers, revokeBulkEditor } from '../api/users';
import { DataGrid } from './DataGrid';
import { useDialog } from './Dialog';
import { ErrorBanner } from './ErrorBanner';
import { UserPicker, type UserSelection } from './UserPicker';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

/** Group badges shown in a row before the rest become a count. Three fits the column at any usual width. */
const MaxGroupBadges = 3;

interface UsersAdminPanelProps {
  me: CurrentUser | null;
  onPermissionsChanged: () => void;
}

/**
 * Membership is edited from here but not owned here: every change goes through the group member endpoints. A
 * shared or super-admin access is deliberately not editable - it is a property of a grant or of a role.
 */
export function UsersAdminPanel({ me, onPermissionsChanged }: UsersAdminPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const dialog = useDialog();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [filter, setFilter] = useState('');
  // The list is already in memory, but filtering it re-renders every row: debouncing keeps typing smooth.
  const debouncedFilter = useDebouncedValue(filter.trim(), 200);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [invitee, setInvitee] = useState<UserSelection | null>(null);
  const [inviteeGroupId, setInviteeGroupId] = useState('');
  const [inviteeRole, setInviteeRole] = useState<GroupRole>('Member');

  const [extraGroupId, setExtraGroupId] = useState('');
  const [extraRole, setExtraRole] = useState<GroupRole>('Member');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedUsers, loadedGroups] = await Promise.all([listUsers(), listGroups()]);
      setUsers(loadedUsers);
      setGroups(loadedGroups);
      setError(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await action();
      setError(null);
      await reload();
      onPermissionsChanged();
      toast.success(done);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function handleAddUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (invitee === null || !inviteeGroupId) {
      return;
    }

    await run(async () => {
      await addGroupMember(inviteeGroupId, invitee.userId, inviteeRole, invitee.displayName ?? undefined);
      setSelectedUserId(invitee.userId);
      setInvitee(null);
      setInviteeRole('Member');
    }, t('toast.memberAdded'));
  }

  async function handleAddToGroup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected || !extraGroupId) {
      return;
    }

    await run(async () => {
      await addGroupMember(extraGroupId, selected.userId, extraRole, selected.displayName ?? undefined);
      setExtraGroupId('');
      setExtraRole('Member');
    }, t('toast.memberAdded'));
  }

  async function handleRemove(user: ManagedUser, membership: ManagedUserGroup) {
    // Leaving a group you administer is legitimate, but rarely what somebody meant to click.
    if (user.isMe) {
      const confirmed = await dialog.confirm({
        title: t('admin.users.removeSelfTitle'),
        body: t('admin.users.removeSelfBody', { group: membership.groupName }),
        confirmLabel: t('common.remove'),
        icon: 'pi-user-minus',
      });

      if (!confirmed) {
        return;
      }
    }

    await run(() => removeGroupMember(membership.groupId, user.userId), t('toast.memberRemoved'));
  }

  function describeAccess(group: ManagedUserGroup): string {
    switch (group.source) {
      case 'Member':
        return t('admin.users.describeMember', { group: group.groupName });
      case 'Shared':
        return t('admin.users.describeShared', {
          group: group.groupName,
          via: group.viaGroupName ?? t('common.group'),
          access: t(`admin.permissions.${group.access}`),
        });
      case 'SuperAdmin':
        return t('admin.users.describeSuperAdmin');
    }
  }

  // The API restricts the list to what the caller administers, but the pickers read canAdminister all the same:
  // a group the caller only reaches through a grant is not theirs to add anybody to.
  const administeredGroups = groups.filter((group) => group.canAdminister);
  const canGrantSuperAdmin = me?.isSuperAdmin === true;
  const assignableRoles: GroupRole[] = canGrantSuperAdmin
    ? ['Member', 'GroupAdmin', 'SuperAdmin']
    : ['Member', 'GroupAdmin'];

  const filtered = useMemo(() => {
    const term = debouncedFilter.toLowerCase();
    if (!term) {
      return users;
    }

    return users.filter((user) =>
      [user.displayName ?? '', user.userId, ...user.groups.map((group) => group.groupName)]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [users, debouncedFilter]);

  const selected = users.find((user) => user.userId === selectedUserId) ?? null;
  const alreadyIn = new Set(selected?.groups.map((group) => group.groupId) ?? []);
  const joinableGroups = administeredGroups.filter((group) => !alreadyIn.has(group.id));

  const userColumns = useMemo<ColDef<ManagedUser>[]>(
    () => [
      {
        field: 'displayName',
        headerName: t('common.user'),
        flex: 2,
        minWidth: 180,
        cellRenderer: ({ data }: ICellRendererParams<ManagedUser>) =>
          data && (
            <span className="d-flex align-items-center gap-2 w-100">
              {/* The object id is not shown - it is machine identity, not a name. Somebody whose name was
                  never cached is the exception: without it the row would identify nobody. */}
              <span className={data.displayName ? 'text-truncate cell-truncate' : 'mono text-truncate cell-truncate'}>
                {data.displayName ?? data.userId}
              </span>
              {data.isMe && (
                <span className="badge badge-neutral flex-shrink-0">{t('common.you')}</span>
              )}
              {data.isSuperAdmin && (
                <span className="badge badge-accent flex-shrink-0">
                  <i className="pi pi-shield me-1" aria-hidden="true" />
                  {t('admin.users.superAdmin')}
                </span>
              )}
              {data.isBulkEditor && (
                <span className="badge badge-accent flex-shrink-0">
                  <i className="pi pi-file-import me-1" aria-hidden="true" />
                  {t('admin.users.bulkEditor')}
                </span>
              )}
            </span>
          ),
      },
      {
        colId: 'groups',
        headerName: t('admin.users.groups'),
        flex: 3,
        minWidth: 240,
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<ManagedUser>) => {
          if (!data) {
            return null;
          }

          // One line, a fixed number of badges, and a count for the rest: letting them wrap meant asking
          // ag-grid to measure the cell and grow the row, which left the column blank.
          const shown = data.groups.slice(0, MaxGroupBadges);
          const overflow = data.groups.length - shown.length;

          return (
            <span className="badge-list">
              {shown.map((group) => (
                <span
                  key={group.groupId}
                  className={
                    group.source === 'Member'
                      ? 'badge badge-accent'
                      : 'badge badge-neutral'
                  }
                  title={describeAccess(group)}
                >
                  <i
                    className={`pi ${group.source === 'Member' ? 'pi-user' : 'pi-share-alt'} me-1`}
                    aria-hidden="true"
                  />
                  {group.groupName} -{' '}
                  {group.source === 'Member' && group.role !== null
                    ? t(`admin.roles.${group.role}`)
                    : t(`admin.permissions.${group.access}`)}
                </span>
              ))}
              {overflow > 0 && (
                <span
                  className="badge badge-neutral"
                  title={data.groups
                    .slice(MaxGroupBadges)
                    .map((group) => group.groupName)
                    .join(', ')}
                >
                  {t('admin.users.moreGroups', { count: overflow })}
                </span>
              )}
              {data.hiddenGroupCount > 0 && (
                <span className="badge badge-neutral" title={t('admin.users.hiddenGroupsTitle')}>
                  <i className="pi pi-eye-slash me-1" aria-hidden="true" />
                  {t('admin.users.hiddenGroups', { count: data.hiddenGroupCount })}
                </span>
              )}
            </span>
          );
        },
      },
      {
        colId: 'actions',
        headerName: t('common.actions'),
        sortable: false,
        width: 130,
        cellRenderer: ({ data }: ICellRendererParams<ManagedUser>) =>
          data && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setSelectedUserId(data.userId)}
            >
              <i className="pi pi-cog me-1" aria-hidden="true" />
              {t('common.manage')}
            </button>
          ),
      },
    ],
    // describeAccess only reads `t`, so the columns are stable between renders of the same language.
    [t],
  );

  const membershipColumns = useMemo<ColDef<ManagedUserGroup>[]>(
    () => [
      { field: 'groupName', headerName: t('common.group'), flex: 2, minWidth: 140 },
      {
        colId: 'how',
        headerName: t('admin.users.how'),
        flex: 2,
        minWidth: 170,
        valueGetter: ({ data }) => {
          if (data?.source === 'Member') {
            return t('admin.users.sourceMember');
          }

          if (data?.source === 'Shared') {
            return t('admin.users.sourceShared', { group: data.viaGroupName ?? t('common.group') });
          }

          return t('admin.users.sourceSuperAdmin');
        },
      },
      {
        colId: 'role',
        headerName: t('common.role'),
        width: 190,
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<ManagedUserGroup>) => {
          if (!selected || data?.source !== 'Member' || data.role === null) {
            return t('common.none');
          }

          return (
            <select
              className="form-select form-select-sm"
              aria-label={t('admin.users.roleOfIn', {
                user: selected.displayName ?? selected.userId,
                group: data.groupName,
              })}
              value={data.role}
              disabled={busy}
              onChange={(event) =>
                void run(() =>
                  updateGroupMemberRole(data.groupId, selected.userId, event.target.value as GroupRole),
                  t('toast.roleChanged'),
                )
              }
            >
              {/* A super admin keeps the option visible even to an admin who cannot grant it, so their role
                  is never silently misrepresented. */}
              {(data.role === 'SuperAdmin' && !canGrantSuperAdmin
                ? [...assignableRoles, 'SuperAdmin' as GroupRole]
                : assignableRoles
              ).map((role) => (
                <option key={role} value={role}>
                  {t(`admin.roles.${role}`)}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        colId: 'access',
        headerName: t('admin.users.access'),
        width: 130,
        valueGetter: ({ data }) => (data ? t(`admin.permissions.${data.access}`) : ''),
      },
      {
        colId: 'since',
        headerName: t('admin.users.memberSince'),
        width: 150,
        valueGetter: ({ data }) =>
          formatDate(data?.memberSince, t('common.none')),
      },
      {
        colId: 'actions',
        headerName: t('common.actions'),
        sortable: false,
        width: 130,
        cellRenderer: ({ data }: ICellRendererParams<ManagedUserGroup>) => {
          // Nothing to offer on a shared or super-admin access: it is withdrawn where it comes from.
          if (!selected || data?.source !== 'Member') {
            return t('common.none');
          }

          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              disabled={busy}
              onClick={() => void handleRemove(selected, data)}
            >
              <i className="pi pi-user-minus me-1" aria-hidden="true" />
              {t('common.remove')}
            </button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, selected, busy, canGrantSuperAdmin],
  );

  return (
    <>
      <ErrorBanner error={error} />

      <div className="card">
        <h2>
          <i className="pi pi-users me-2" aria-hidden="true" />
          {t('admin.users.heading')}
        </h2>
        <p className="hint">{t('admin.users.intro')}</p>

        <div className="toolbar">
          <div className="field flex-grow-1">
            <label htmlFor="user-filter">
              <i className="pi pi-filter me-1" aria-hidden="true" />
              {t('common.filter')}
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="pi pi-search" aria-hidden="true" />
              </span>
              <input
                id="user-filter"
                type="search"
                className="form-control"
                value={filter}
                placeholder={t('admin.users.filterPlaceholder')}
                autoComplete="off"
                onChange={(event) => setFilter(event.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <p>
            <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
            {t('app.loading')}
          </p>
        ) : users.length === 0 ? (
          <div className="alert alert-info d-flex gap-2 mb-0" role="status">
            <i className="pi pi-info-circle mt-1" aria-hidden="true" />
            <span>{t('admin.users.none')}</span>
          </div>
        ) : filtered.length === 0 ? (
          <p className="hint">{t('admin.users.noMatch', { term: debouncedFilter })}</p>
        ) : (
          <DataGrid
            rows={filtered}
            columns={userColumns}
            getRowId={(user) => user.userId}
            label={t('admin.users.heading')}
            emptyMessage={t('admin.users.none')}
            rowHeight={48}
          />
        )}
      </div>

      {selected && (
        <div className="card">
          <h2>
            <i className="pi pi-user me-2" aria-hidden="true" />
            {selected.displayName ?? selected.userId}
          </h2>
          {selected.isSuperAdmin && (
            <div className="alert alert-info d-flex gap-2" role="status">
              <i className="pi pi-shield mt-1" aria-hidden="true" />
              <span>{t('admin.users.superAdminNotice')}</span>
            </div>
          )}

          {canGrantSuperAdmin && (
            <div className="form-check form-switch mb-3">
              <input
                id="bulk-editor-switch"
                type="checkbox"
                role="switch"
                className="form-check-input"
                checked={selected.isBulkEditor}
                disabled={busy}
                onChange={(event) =>
                  void run(
                    () => (event.target.checked ? grantBulkEditor : revokeBulkEditor)(selected.userId),
                    t(event.target.checked ? 'toast.bulkEditorGranted' : 'toast.bulkEditorRevoked'),
                  )
                }
              />
              <label className="form-check-label" htmlFor="bulk-editor-switch">
                {t('admin.users.bulkEditorSwitch')}
              </label>
              <p className="hint mb-0">{t('admin.users.bulkEditorHint')}</p>
            </div>
          )}

          <h3>{t('admin.users.groups')}</h3>
          <DataGrid
            rows={selected.groups}
            columns={membershipColumns}
            getRowId={(group) => group.groupId}
            label={t('admin.users.groups')}
            emptyMessage={t('admin.groups.noGroup')}
          />

          {selected.hiddenGroupCount > 0 && (
            <p className="hint">
              <i className="pi pi-eye-slash me-1" aria-hidden="true" />
              {t('admin.users.alsoReaches', { count: selected.hiddenGroupCount })}
            </p>
          )}

          {joinableGroups.length > 0 && (
            <form
              onSubmit={handleAddToGroup}
              aria-label={t('admin.users.addToAnotherGroup')}
              className="mt-3"
            >
              <h3>
                <i className="pi pi-user-plus me-2" aria-hidden="true" />
                {t('admin.users.addToAnotherGroup')}
              </h3>
              <div className="toolbar">
                <div className="field">
                  <label htmlFor="extra-group">
                    <i className="pi pi-users me-1" aria-hidden="true" />
                    {t('common.group')}
                  </label>
                  <select
                    id="extra-group"
                    className="form-select"
                    value={extraGroupId}
                    onChange={(event) => setExtraGroupId(event.target.value)}
                  >
                    <option value="">{t('admin.users.selectGroup')}</option>
                    {joinableGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="extra-role">
                    <i className="pi pi-shield me-1" aria-hidden="true" />
                    {t('common.role')}
                  </label>
                  <select
                    id="extra-role"
                    className="form-select"
                    value={extraRole}
                    onChange={(event) => setExtraRole(event.target.value as GroupRole)}
                  >
                    {assignableRoles.map((role) => (
                      <option key={role} value={role}>
                        {t(`admin.roles.${role}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary align-self-end"
                  disabled={busy || !extraGroupId}
                >
                  <i className="pi pi-user-plus me-2" aria-hidden="true" />
                  {t('admin.users.addToGroup')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="card">
        <h2>
          <i className="pi pi-user-plus me-2" aria-hidden="true" />
          {t('admin.users.addUser')}
        </h2>
        {administeredGroups.length === 0 ? (
          <div className="alert alert-info d-flex gap-2 mb-0" role="status">
            <i className="pi pi-info-circle mt-1" aria-hidden="true" />
            <span>{t('admin.users.noAdministeredGroup')}</span>
          </div>
        ) : (
          <>
            <p className="hint">{t('admin.users.addUserIntro')}</p>
            <form onSubmit={handleAddUser} aria-label={t('admin.users.addUser')}>
              <UserPicker selection={invitee} onChange={setInvitee} disabled={busy} />
              <div className="toolbar">
                <div className="field">
                  <label htmlFor="invitee-group">
                    <i className="pi pi-users me-1" aria-hidden="true" />
                    {t('common.group')}
                  </label>
                  <select
                    id="invitee-group"
                    className="form-select"
                    value={inviteeGroupId}
                    onChange={(event) => setInviteeGroupId(event.target.value)}
                  >
                    <option value="">{t('admin.users.selectGroup')}</option>
                    {administeredGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="invitee-role">
                    <i className="pi pi-shield me-1" aria-hidden="true" />
                    {t('common.role')}
                  </label>
                  <select
                    id="invitee-role"
                    className="form-select"
                    value={inviteeRole}
                    onChange={(event) => setInviteeRole(event.target.value as GroupRole)}
                  >
                    {assignableRoles.map((role) => (
                      <option key={role} value={role}>
                        {t(`admin.roles.${role}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary align-self-end"
                  disabled={busy || invitee === null || !inviteeGroupId}
                >
                  <i className="pi pi-user-plus me-2" aria-hidden="true" />
                  {t('admin.users.addUser')}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
